import { useEffect, useMemo, useState, useCallback, type ReactNode } from 'react';
import { SectionSurface } from '../../components/role-ui';
import type { CargaGasolina, Conductor, FormaPagoCargaGasolina, Vehiculo } from '../../lib/appTypes';
import { getCargasGasolina, exportarCargasGasolina, getExportarCargasGasolinaStatus, adminEditCargaGasolina } from '../../lib/storage';
import { Modal, SkeletonRows } from '../../components/ui-kit';

const PAGE_SIZE = 10;
const POLL_INTERVAL_MS = 2000;

function PanelPopup({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <Modal onClose={onClose} maxWidthClassName="max-w-3xl">
      <div className="px-6 pb-6 pt-14">{children}</div>
    </Modal>
  );
}

function formatDateTime(timestamp?: number): string {
  if (!timestamp) return '—';
  return new Intl.DateTimeFormat('es-EC', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

function formatCurrency(value?: number): string {
  if (typeof value !== 'number') return '—';
  return new Intl.NumberFormat('es-EC', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function formaPagoLabel(formaPago: CargaGasolina['formaPago']): string {
  if (formaPago === 'tc') return 'Tarjeta de crédito';
  if (formaPago === 'efectivo') return 'Efectivo';
  return '—';
}

function getVehiculoLabel(carga: CargaGasolina, vehiculos: Vehiculo[]): string {
  const vehiculo = carga.vehiculo ?? vehiculos.find(item => item.id === carga.vehiculoId);
  if (!vehiculo) return `Vehículo ${carga.vehiculoId}`;
  return [vehiculo.placa, vehiculo.marca, vehiculo.modelo].filter(Boolean).join(' · ');
}

function getVehiculoPlaca(carga: CargaGasolina, vehiculos: Vehiculo[]): string {
  const vehiculo = carga.vehiculo ?? vehiculos.find(item => item.id === carga.vehiculoId);
  return vehiculo?.placa || `Vehículo ${carga.vehiculoId}`;
}

function getConductorLabel(carga: CargaGasolina, conductores: Conductor[]): string {
  return carga.conductorNombre
    || conductores.find(item => item.id === carga.conductorId)?.nombre
    || `Conductor ${carga.conductorId}`;
}

type Props = {
  conductores: Conductor[];
  vehiculos: Vehiculo[];
};

export default function AdminGasolinaHistoryPanel({ conductores, vehiculos }: Props) {
  const [cargas, setCargas] = useState<CargaGasolina[]>([]);
  const [pagina, setPagina] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [detalle, setDetalle] = useState<CargaGasolina | null>(null);

  // Edición
  const [editandoCarga, setEditandoCarga] = useState<CargaGasolina | null>(null);
  const [editFormaPago, setEditFormaPago] = useState<FormaPagoCargaGasolina>('efectivo');
  const [editUltimosCuatro, setEditUltimosCuatro] = useState('');
  const [editNumeroVoucher, setEditNumeroVoucher] = useState('');
  const [editGalones, setEditGalones] = useState('');
  const [editTotalPagado, setEditTotalPagado] = useState('');
  const [editando, setEditando] = useState(false);

  // Filtros
  const [filtroPlaca, setFiltroPlaca] = useState('');
  const [filtroUltimosCuatro, setFiltroUltimosCuatro] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');

  // Exportación
  const [exportando, setExportando] = useState(false);
  const [exportTaskId, setExportTaskId] = useState<string | null>(null);
  const [exportMensaje, setExportMensaje] = useState('');

  const loadData = useCallback(async (pageNum: number, placa: string, ultimosCuatro: string, fechaDesde: string, fechaHasta: string) => {
    try {
      setCargando(true);
      const params: Record<string, string | number> = {
        page: pageNum,
        page_size: PAGE_SIZE,
      };
      if (placa.trim()) params.placa = placa.trim();
      if (ultimosCuatro.trim()) params.ultimos_cuatro = ultimosCuatro.trim();
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;

      const result = await getCargasGasolina(params);
      setCargas(result.items);
      setTotalRegistros(result.count);
      setError('');
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'No se pudo cargar el historial de gasolina.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void loadData(pagina, filtroPlaca, filtroUltimosCuatro, filtroFechaDesde, filtroFechaHasta);
  }, [pagina, loadData, filtroPlaca, filtroUltimosCuatro, filtroFechaDesde, filtroFechaHasta]);

  const handleBuscar = () => {
    setPagina(1);
    void loadData(1, filtroPlaca, filtroUltimosCuatro, filtroFechaDesde, filtroFechaHasta);
  };

  const handleLimpiarFiltros = () => {
    setFiltroPlaca('');
    setFiltroUltimosCuatro('');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
    setPagina(1);
  };

  const handleExportar = async (formato: 'excel' | 'pdf') => {
    try {
      setExportando(true);
      setExportMensaje('Iniciando exportación...');
      const params: Record<string, string> = {};
      if (filtroPlaca.trim()) params.placa = filtroPlaca.trim();
      if (filtroUltimosCuatro.trim()) params.ultimos_cuatro = filtroUltimosCuatro.trim();
      if (filtroFechaDesde) params.fecha_desde = filtroFechaDesde;
      if (filtroFechaHasta) params.fecha_hasta = filtroFechaHasta;

      const result = await exportarCargasGasolina(formato, params);
      setExportTaskId(result.task_id);
      setExportMensaje('Generando archivo...');

      // Poll until done
      const poll = async () => {
        const status = await getExportarCargasGasolinaStatus(result.task_id);
        if (status.status === 'SUCCESS' && status.file_url) {
          setExportando(false);
          setExportTaskId(null);
          setExportMensaje('');
          // Descargar directamente desde la URL pública de MinIO
          const link = document.createElement('a');
          link.href = status.file_url;
          link.download = status.file_name || `cargas_gasolina.${formato === 'excel' ? 'xlsx' : 'pdf'}`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          link.remove();
        } else if (status.status === 'FAILURE') {
          setExportando(false);
          setExportTaskId(null);
          setExportMensaje('');
          setError(status.error || 'Error al generar la exportación.');
        } else {
          setTimeout(() => { void poll(); }, POLL_INTERVAL_MS);
        }
      };

      setTimeout(() => { void poll(); }, POLL_INTERVAL_MS);
    } catch (err: unknown) {
      setExportando(false);
      setExportTaskId(null);
      setExportMensaje('');
      setError((err as { message?: string })?.message ?? 'No se pudo iniciar la exportación.');
    }
  };

  const handleAbrirEdicion = (carga: CargaGasolina) => {
    setEditandoCarga(carga);
    setEditFormaPago(carga.formaPago);
    setEditUltimosCuatro(carga.ultimosCuatroTarjeta || '');
    setEditNumeroVoucher(carga.numeroVoucher || '');
    setEditGalones(String(carga.cantidadGalones ?? ''));
    setEditTotalPagado(String(carga.totalPagado ?? ''));
    setEditando(false);
  };

  const handleGuardarEdicion = async () => {
    if (!editandoCarga) return;
    const galones = Number(editGalones);
    const total = Number(editTotalPagado);
    if (!galones || galones <= 0) {
      setError('Ingresa una cantidad de galones válida.');
      return;
    }
    if (!total || total <= 0) {
      setError('Ingresa un total pagado válido.');
      return;
    }
    if (editFormaPago === 'tc') {
      if (!editNumeroVoucher.trim()) {
        setError('Ingresa el número de factura o voucher.');
        return;
      }
      if (!/^\d{4}$/.test(editUltimosCuatro.trim())) {
        setError('Ingresa los últimos 4 dígitos de la tarjeta.');
        return;
      }
    }
    try {
      setEditando(true);
      const actualizada = await adminEditCargaGasolina(editandoCarga.id, {
        formaPago: editFormaPago,
        ultimosCuatroTarjeta: editFormaPago === 'tc' ? editUltimosCuatro.trim() : undefined,
        numeroVoucher: editNumeroVoucher.trim(),
        cantidadGalones: galones,
        totalPagado: total,
      });
      setCargas(prev => prev.map(c => c.id === actualizada.id ? actualizada : c));
      setEditandoCarga(null);
      setEditFormaPago('efectivo');
      setEditUltimosCuatro('');
      setEditNumeroVoucher('');
      setEditGalones('');
      setEditTotalPagado('');
      setError('');
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'No se pudo actualizar la carga de gasolina.');
    } finally {
      setEditando(false);
    }
  };

  const totalPaginas = useMemo(() => Math.max(1, Math.ceil(totalRegistros / PAGE_SIZE)), [totalRegistros]);
  const paginaActual = Math.min(pagina, totalPaginas);

  useEffect(() => {
    setPagina(current => Math.min(current, totalPaginas));
  }, [totalPaginas]);

  const inputCls =
    'w-full rounded-[14px] border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100';
  const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500';

  return (
    <>
      <section className="space-y-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-end">
          <p className="text-sm text-stone-500">{totalRegistros} registros</p>
        </div>

        {/* Filtros y exportación */}
        <div className="ui-filtros rounded-[var(--radius-card)] border border-stone-200 bg-stone-50 p-4">
          <div className="ui-filtro-busqueda">
            <label className="ui-filtro-label">Placa</label>
            <input
              type="text"
              value={filtroPlaca}
              onChange={e => setFiltroPlaca(e.target.value)}
              placeholder="Buscar por placa"
              className={inputCls}
            />
          </div>
          <div>
            <label className="ui-filtro-label">Últ. 4 díg. tarjeta</label>
            <input
              type="text"
              value={filtroUltimosCuatro}
              onChange={e => setFiltroUltimosCuatro(e.target.value)}
              placeholder="Ej: 1234"
              maxLength={4}
              className={inputCls}
            />
          </div>
          <div>
            <label className="ui-filtro-label">Fecha desde</label>
            <input
              type="date"
              value={filtroFechaDesde}
              onChange={e => setFiltroFechaDesde(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="ui-filtro-label">Fecha hasta</label>
            <input
              type="date"
              value={filtroFechaHasta}
              onChange={e => setFiltroFechaHasta(e.target.value)}
              className={inputCls}
            />
          </div>
          <div className="ui-filtro-acciones ui-filtro-acciones-fila">
          <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleBuscar}
            disabled={cargando}
            className="ui-btn ui-btn-primary"
          >
            Buscar
          </button>
          <button
            type="button"
            onClick={handleLimpiarFiltros}
            className="ui-btn ui-btn-secondary"
          >
            Limpiar filtros
          </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void handleExportar('excel')}
              disabled={exportando}
              className="ui-btn ui-btn-success"
            >
              Exportar Excel
            </button>
            <button
              type="button"
              onClick={() => void handleExportar('pdf')}
              disabled={exportando}
              className="ui-btn ui-btn-info"
            >
              Exportar PDF
            </button>
          </div>
          </div>
        </div>

        {exportMensaje && (
          <div className="rounded-[var(--radius-panel)] border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-700 shadow-sm">
            {exportMensaje}
          </div>
        )}

        {error && (
          <div className="rounded-[var(--radius-panel)] border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-sm">
            {error}
          </div>
        )}

        <SectionSurface className="space-y-5">
          {cargando ? (
            <SkeletonRows etiqueta="Cargando historial de gasolina..." />
          ) : cargas.length === 0 ? (
            <div className="rounded-[var(--radius-card)] border border-dashed border-stone-300 bg-white/70 p-10 text-center text-stone-500">
              No hay cargas de gasolina registradas.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="ui-tabla min-w-full text-left text-sm text-stone-600">
                  <thead className="bg-stone-50 text-xs uppercase tracking-[0.16em] text-stone-500">
                    <tr>
                      <th className="px-5 py-3 font-semibold">Fecha</th>
                      <th className="px-5 py-3 font-semibold">Placa</th>
                      <th className="px-5 py-3 font-semibold">Conductor</th>
                      <th className="px-5 py-3 font-semibold">Forma de pago</th>
                      <th className="px-5 py-3 font-semibold">N° Voucher</th>
                      <th className="px-5 py-3 font-semibold text-right">Galones</th>
                      <th className="px-5 py-3 font-semibold text-right">Total pagado</th>
                      <th className="px-5 py-3 font-semibold text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cargas.map(carga => (
                      <tr key={carga.id} className="border-t border-stone-100 align-top">
                        <td className="px-5 py-4 whitespace-nowrap text-stone-700">{formatDateTime(carga.fechaRegistro)}</td>
                        <td className="px-5 py-4 font-semibold">{getVehiculoPlaca(carga, vehiculos)}</td>
                        <td className="px-5 py-4">{getConductorLabel(carga, conductores)}</td>
                        <td className="px-5 py-4">{formaPagoLabel(carga.formaPago)}</td>
                        <td className="px-5 py-4">{carga.numeroVoucher || '—'}</td>
                        <td className="px-5 py-4 text-right">{carga.cantidadGalones?.toFixed(2)}</td>
                        <td className="px-5 py-4 text-right font-semibold">{formatCurrency(carga.totalPagado)}</td>
                        <td className="px-5 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleAbrirEdicion(carga)}
                              className="ui-btn ui-btn-warning ui-btn-sm"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => setDetalle(carga)}
                              className="ui-btn ui-btn-info ui-btn-sm"
                            >
                              Ver detalles
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-200 px-1 pt-4 text-sm text-stone-500">
                <p>
                  Mostrando {Math.min((paginaActual - 1) * PAGE_SIZE + 1, totalRegistros)}-
                  {Math.min(paginaActual * PAGE_SIZE, totalRegistros)} de {totalRegistros}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPagina(current => Math.max(1, current - 1))}
                    disabled={paginaActual === 1}
                    className="rounded-2xl border border-stone-200 px-3 py-2 font-medium text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span>Página {paginaActual} de {totalPaginas}</span>
                  <button
                    type="button"
                    onClick={() => setPagina(current => Math.min(totalPaginas, current + 1))}
                    disabled={paginaActual === totalPaginas}
                    className="rounded-2xl border border-stone-200 px-3 py-2 font-medium text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </SectionSurface>
      </section>

      {detalle && (
        <PanelPopup onClose={() => setDetalle(null)}>
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-semibold text-stone-900">Detalle de carga de gasolina</h3>
              <p className="text-sm text-stone-500">Revisa los datos registrados por el conductor y el soporte del voucher.</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[var(--radius-card)] border border-stone-200 bg-stone-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Conductor</p>
                <p className="mt-2 text-sm font-medium text-stone-900">{getConductorLabel(detalle, conductores)}</p>
              </div>
              <div className="rounded-[var(--radius-card)] border border-stone-200 bg-stone-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Vehículo</p>
                <p className="mt-2 text-sm font-medium text-stone-900">{getVehiculoLabel(detalle, vehiculos)}</p>
              </div>
              <div className="rounded-[var(--radius-card)] border border-stone-200 bg-stone-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Fecha de creación</p>
                <p className="mt-2 text-sm font-medium text-stone-900">{formatDateTime(detalle.fechaRegistro)}</p>
              </div>
              <div className="rounded-[var(--radius-card)] border border-stone-200 bg-stone-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Forma de pago</p>
                <p className="mt-2 text-sm font-medium text-stone-900">{formaPagoLabel(detalle.formaPago)}</p>
              </div>
              <div className="rounded-[var(--radius-card)] border border-stone-200 bg-stone-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Número de voucher</p>
                <p className="mt-2 text-sm font-medium text-stone-900">{detalle.numeroVoucher || '—'}</p>
              </div>
              <div className="rounded-[var(--radius-card)] border border-stone-200 bg-stone-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Últimos 4 tarjeta</p>
                <p className="mt-2 text-sm font-medium text-stone-900">{detalle.ultimosCuatroTarjeta || '—'}</p>
              </div>
              <div className="rounded-[var(--radius-card)] border border-stone-200 bg-stone-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Cantidad en galones</p>
                <p className="mt-2 text-sm font-medium text-stone-900">{detalle.cantidadGalones}</p>
              </div>
              <div className="rounded-[var(--radius-card)] border border-stone-200 bg-stone-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Total pagado</p>
                <p className="mt-2 text-sm font-medium text-stone-900">{formatCurrency(detalle.totalPagado)}</p>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Foto del voucher</p>
              {detalle.imgFacturavou ? (
                <div className="overflow-hidden rounded-[var(--radius-card)] border border-stone-200 bg-stone-50/80">
                  <img src={detalle.imgFacturavou} alt="Voucher de gasolina" className="h-72 w-full object-cover" />
                  <div className="px-4 py-3">
                    <a
                      href={detalle.imgFacturavou}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-blue-700 underline decoration-blue-200 underline-offset-4 transition hover:text-blue-900"
                    >
                      Abrir imagen en otra pestaña
                    </a>
                  </div>
                </div>
              ) : (
                <div className="rounded-[var(--radius-card)] border border-dashed border-stone-300 bg-white/70 p-6 text-sm text-stone-500">
                  Esta carga no tiene voucher asociado.
                </div>
              )}
            </div>
          </div>
        </PanelPopup>
      )}

      {editandoCarga && (
        <PanelPopup onClose={() => { setEditandoCarga(null); setEditFormaPago('efectivo'); setEditUltimosCuatro(''); setEditNumeroVoucher(''); setEditGalones(''); setEditTotalPagado(''); }}>
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-semibold text-stone-900">Editar carga de gasolina</h3>
              <p className="text-sm text-stone-500">
                {getVehiculoPlaca(editandoCarga, vehiculos)} · {getConductorLabel(editandoCarga, conductores)} · {formatDateTime(editandoCarga.fechaRegistro)}
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className={labelCls}>Forma de pago</label>
                <select
                  value={editFormaPago}
                  onChange={e => {
                    setEditFormaPago(e.target.value as FormaPagoCargaGasolina);
                    setEditUltimosCuatro('');
                  }}
                  className={inputCls}
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="tc">Tarjeta de crédito</option>
                </select>
              </div>
              {editFormaPago === 'tc' && (
                <div>
                  <label className={labelCls}>Últimos 4 dígitos de la tarjeta</label>
                  <input
                    type="text"
                    value={editUltimosCuatro}
                    onChange={e => setEditUltimosCuatro(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    className={inputCls}
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="1234"
                  />
                </div>
              )}
              <div>
                <label className={labelCls}>N° factura / voucher</label>
                <input
                  type="text"
                  value={editNumeroVoucher}
                  onChange={e => setEditNumeroVoucher(e.target.value.slice(0, 120))}
                  className={inputCls}
                  maxLength={120}
                  placeholder="Ej: 001-002-000123"
                />
              </div>
              <div>
                <label className={labelCls}>Cantidad en galones</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={editGalones}
                  onChange={e => setEditGalones(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Total pagado</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={editTotalPagado}
                  onChange={e => setEditTotalPagado(e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setEditandoCarga(null); setEditFormaPago('efectivo'); setEditUltimosCuatro(''); setEditNumeroVoucher(''); setEditGalones(''); setEditTotalPagado(''); }}
                className="ui-btn ui-btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleGuardarEdicion()}
                disabled={editando}
                className="ui-btn ui-btn-primary"
              >
                {editando ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </PanelPopup>
      )}
    </>
  );
}